import React from "react";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
  useMediaQuery,
  Paper,
  Chip,
} from "@mui/material";
import {
  AddOutlined,
  EditOutlined,
  DeleteOutlined,
  SmartToy,
  LinkOutlined,
  KeyOutlined,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import CommonDialog from "../../common/CommonDialog";
import PageHeader from "../../common/PageHeader";

const emptyForm = {
  name: "",
  baseUrl: "",
  apiKey: "",
  enabled: true,
};

function maskApiKey(key) {
  const value = String(key || "");
  if (value.length <= 8) return "••••••••";
  return `${value.slice(0, 4)}••••${value.slice(-4)}`;
}

export default function BeyundAiProviders({ lang }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { hitAxios } = React.useContext(GlobalContext);

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await hitAxios({
        path: "/api/beyund-ai-provider/list",
        post: false,
        admin: true,
        showLoading: false,
      });
      if (res?.data?.success) {
        setRows(Array.isArray(res.data.data) ? res.data.data : []);
      }
    } finally {
      setLoading(false);
    }
  }, [hitAxios]);

  React.useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      baseUrl: row.baseUrl || "",
      apiKey: row.apiKey || "",
      enabled: Boolean(row.enabled),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const path = editingId
        ? "/api/beyund-ai-provider/update"
        : "/api/beyund-ai-provider/add";
      const obj = editingId ? { id: editingId, ...form } : form;
      const res = await hitAxios({
        path,
        post: true,
        admin: true,
        obj,
        showLoading: false,
      });
      if (res?.data?.success) {
        setDialogOpen(false);
        setForm(emptyForm);
        setEditingId(null);
        await load();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(lang?.confirmDelete || "Delete this provider?")) return;
    const res = await hitAxios({
      path: "/api/beyund-ai-provider/delete",
      post: true,
      admin: true,
      obj: { id },
      showLoading: false,
    });
    if (res?.data?.success) await load();
  };

  const handleToggle = async (row) => {
    const next = !Boolean(row.enabled);
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, enabled: next ? 1 : 0 } : r)),
    );
    const res = await hitAxios({
      path: "/api/beyund-ai-provider/toggle",
      post: true,
      admin: true,
      obj: { id: row.id, enabled: next },
      showLoading: false,
      showSnackbar: true,
    });
    if (!res?.data?.success) await load();
  };

  return (
    <Box>
      <PageHeader
        title={lang?.beyundAiProviders || "Beyund AI Providers"}
        subtitle={
          lang?.beyundAiProvidersSubtitle ||
          "Manage provider base URLs, API keys, and enabled status."
        }
        icon={SmartToy}
        primaryAction={
          <Button variant="contained" startIcon={<AddOutlined />} onClick={openAdd}>
            {lang?.addNew || "Add New"}
          </Button>
        }
      />

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {isMobile ? (
          <Stack spacing={1.5} sx={{ p: 2 }}>
            {rows.map((row) => (
              <Box
                key={row.id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: alpha(theme.palette.background.paper, 0.6),
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography fontWeight={700}>{row.name}</Typography>
                  <Chip
                    size="small"
                    label={row.enabled ? "Enabled" : "Disabled"}
                    color={row.enabled ? "success" : "default"}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                  {row.baseUrl}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  {maskApiKey(row.apiKey)}
                </Typography>
                <Stack direction="row" spacing={1} mt={1.5} alignItems="center">
                  <Switch
                    checked={Boolean(row.enabled)}
                    onChange={() => handleToggle(row)}
                    size="small"
                  />
                  <IconButton size="small" onClick={() => openEdit(row)}>
                    <EditOutlined fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                    <DeleteOutlined fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            ))}
            {!loading && !rows.length && (
              <Typography color="text.secondary" textAlign="center" py={4}>
                No providers yet. Add one to get started.
              </Typography>
            )}
          </Stack>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Base URL</TableCell>
                <TableCell>API Key</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{row.name}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      {row.baseUrl}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {maskApiKey(row.apiKey)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={Boolean(row.enabled)}
                      onChange={() => handleToggle(row)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(row)}>
                      <EditOutlined fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                      <DeleteOutlined fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && !rows.length && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary" textAlign="center" py={4}>
                      No providers yet. Add one to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <CommonDialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        title={editingId ? lang?.editProvider || "Edit Provider" : lang?.addProvider || "Add Provider"}
        icon={editingId ? EditOutlined : AddOutlined}
        maxWidth="sm"
        fullWidth
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Name"
            fullWidth
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. OpenAI Production"
          />
          <TextField
            label="Base URL"
            fullWidth
            value={form.baseUrl}
            onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
            placeholder="https://api.example.com/v1"
            InputProps={{
              startAdornment: <LinkOutlined fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />,
            }}
          />
          <TextField
            label="API Key"
            fullWidth
            value={form.apiKey}
            onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
            placeholder="sk-..."
            InputProps={{
              startAdornment: <KeyOutlined fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />,
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(form.enabled)}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
            }
            label="Enabled"
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1 }}>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {editingId ? "Save Changes" : "Add Provider"}
            </Button>
          </Stack>
        </Stack>
      </CommonDialog>
    </Box>
  );
}
